import { useEffect, useRef } from 'react';
import {
  buildPublishFailureToast,
  isPostPublishInFlight,
} from '../utils/postPublishErrors';

/**
 * Poll while posts are queued/publishing; toast once when a post reaches terminal failed.
 * Does not toast on retry attempts (still publishing) or existing failures on first load.
 */
export function usePublishStatusPolling({
  posts,
  refreshPosts,
  showError,
  enabled = true,
  intervalMs = 8000,
}) {
  const prevSnapshotRef = useRef(new Map());
  const notifiedFailuresRef = useRef(new Set());
  const seededRef = useRef(false);

  const hasInFlight = Array.isArray(posts) && posts.some(isPostPublishInFlight);

  useEffect(() => {
    if (!enabled || !hasInFlight) return undefined;

    let cancelled = false;
    const tick = async () => {
      try {
        await refreshPosts({ silent: true });
      } catch {
        /* polling should not spam toasts */
      }
    };

    tick();
    const intervalId = setInterval(() => {
      if (!cancelled) tick();
    }, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [enabled, hasInFlight, refreshPosts, intervalMs]);

  useEffect(() => {
    if (!Array.isArray(posts)) return;

    posts.forEach((post) => {
      const id = post.id;
      const snap = {
        status: post.status,
        publish_status: post.publish_status,
      };
      const prev = prevSnapshotRef.current.get(id);

      if (prev && seededRef.current) {
        const wasInFlight =
          prev.publish_status === 'queued' || prev.publish_status === 'publishing';
        const nowTerminalFailed =
          post.status === 'failed' && post.publish_status === 'failed';

        if (wasInFlight && nowTerminalFailed) {
          const notifyKey = `fail-${id}-${buildPublishFailureToast(post)}`;
          if (!notifiedFailuresRef.current.has(notifyKey)) {
            notifiedFailuresRef.current.add(notifyKey);
            showError(buildPublishFailureToast(post));
          }
        }
      }

      prevSnapshotRef.current.set(id, snap);
    });

    seededRef.current = true;
  }, [posts, showError]);
}
